// Injects `cursor: none` into Beam widget shadow roots while the custom cursor
// is active, since the global rule can't cross the shadow boundary (Beam items
// would otherwise show their own `cursor: pointer` alongside the custom cursor).
function createBeamCursorBridge() {
  const styleId = 'tarte-custom-cursor-beam-style';
  // The shadow root lives on these <beam-*> hosts, not the wrapper div.
  const beamTags = [
    'beam-select-nonprofit',
    'beam-product-details-page',
    'beam-cumulative-impact',
    'beam-community-impact'
  ];
  const selector = beamTags.join(', ');
  let observer = null;
  let queued = false;
  let active = false;

  const inject = function() {
    if (!active) {
      return;
    }

    document.querySelectorAll(selector).forEach(function(host) {
      const shadow = host.shadowRoot;

      if (!shadow || shadow.getElementById(styleId)) {
        return;
      }

      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = ':host, :host *, * { cursor: none !important; }';
      shadow.appendChild(style);
    });
  };

  const queueInject = function() {
    if (queued || !active) {
      return;
    }

    queued = true;
    window.requestAnimationFrame(function() {
      queued = false;
      inject();
    });
  };

  const activate = function() {
    if (active) {
      return;
    }

    active = true;
    inject();

    // Re-inject once each Beam element is defined (its shadow root attaches then).
    if (window.customElements && customElements.whenDefined) {
      beamTags.forEach(function(tag) {
        customElements.whenDefined(tag)
          .then(queueInject)
          .catch(function() {});
      });
    }

    // Re-inject when the cart drawer re-renders its Beam widget on cart changes;
    // the other widgets mount once and are covered by inject()/whenDefined above.
    const observerRoot = document.querySelector('cart-drawer') || document.body;
    observer = new MutationObserver(queueInject);
    observer.observe(observerRoot, { childList: true, subtree: true });
  };

  const deactivate = function() {
    active = false;

    if (observer) {
      observer.disconnect();
      observer = null;
    }

    document.querySelectorAll(selector).forEach(function(host) {
      const style = host.shadowRoot && host.shadowRoot.getElementById(styleId);
      if (style) {
        style.remove();
      }
    });
  };

  return { activate, deactivate };
}

window.initCustomCursor = function() {
  const config = window.customCursorConfig;

  if (!config?.imageUrl) {
    return;
  }

  if (!window.matchMedia('(pointer: fine)').matches) {
    return;
  }

  const root = document.documentElement;
  const styleId = 'tarte-custom-cursor-style';
  const cursorId = 'tarte-custom-cursor';
  const cursorSize = 32;
  const cursorOffset = cursorSize / 2;
  const maxTimerDuration = 2147483647;
  const beamBridge = createBeamCursorBridge();
  let removalTimer;
  let rafId = null;
  let currentX = -9999;
  let currentY = -9999;
  let cursorEl = null;
  let isRemoved = false;
  const endTimestamp = Number(config.endTimestamp);

  const isExpired = function() {
    return !Number.isNaN(endTimestamp) && endTimestamp > 0 && Date.now() >= (endTimestamp * 1000);
  };

  const renderCursor = function() {
    rafId = null;

    if (!cursorEl || isRemoved) {
      return;
    }

    cursorEl.style.transform = `translate3d(${currentX - cursorOffset}px, ${currentY - cursorOffset}px, 0)`;
  };

  const queueRender = function() {
    if (!rafId) {
      rafId = window.requestAnimationFrame(renderCursor);
    }
  };

  const showCursor = function() {
    if (isExpired()) {
      removeCursor();
      return;
    }

    if (cursorEl && !isRemoved) {
      cursorEl.hidden = false;
    }
  };

  const hideCursor = function() {
    if (cursorEl) {
      cursorEl.hidden = true;
    }
  };

  const handlePointerMove = function(event) {
    if (isExpired()) {
      removeCursor();
      return;
    }

    currentX = event.clientX;
    currentY = event.clientY;
    showCursor();
    queueRender();
  };

  const handleDocumentExit = function(event) {
    if (!event.relatedTarget && !event.toElement) {
      hideCursor();
    }
  };

  const removeCursor = function() {
    isRemoved = true;
    root.classList.remove('tarte-custom-cursor-active');

    beamBridge.deactivate();

    const existingStyle = document.getElementById(styleId);
    if (existingStyle) {
      existingStyle.remove();
    }

    const existingCursor = document.getElementById(cursorId);
    if (existingCursor) {
      existingCursor.remove();
    }

    window.removeEventListener('pointermove', handlePointerMove);
    document.removeEventListener('mouseout', handleDocumentExit);
    document.removeEventListener('mouseover', showCursor);
    document.removeEventListener('focusin', showCursor);

    if (rafId) {
      window.cancelAnimationFrame(rafId);
      rafId = null;
    }

    if (removalTimer) {
      window.clearTimeout(removalTimer);
      removalTimer = null;
    }
  };

  const applyCursor = function() {
    let styleEl = document.getElementById(styleId);

    if (!styleEl) {
      styleEl = document.createElement('style');
      styleEl.id = styleId;
      document.head.appendChild(styleEl);
    }

    styleEl.textContent = `
      html.tarte-custom-cursor-active,
      html.tarte-custom-cursor-active body,
      html.tarte-custom-cursor-active body *,
      html.tarte-custom-cursor-active *::before,
      html.tarte-custom-cursor-active *::after {
        cursor: none !important;
      }
    `;

    cursorEl = document.createElement('img');
    cursorEl.id = cursorId;
    cursorEl.src = config.imageUrl;
    cursorEl.alt = '';
    cursorEl.setAttribute('aria-hidden', 'true');
    cursorEl.decoding = 'async';
    cursorEl.style.position = 'fixed';
    cursorEl.style.left = '0';
    cursorEl.style.top = '0';
    cursorEl.style.width = `${cursorSize}px`;
    cursorEl.style.height = `${cursorSize}px`;
    cursorEl.style.objectFit = 'contain';
    cursorEl.style.pointerEvents = 'none';
    cursorEl.style.zIndex = '2147483647';
    cursorEl.style.transform = 'translate3d(-9999px, -9999px, 0)';
    cursorEl.style.willChange = 'transform';
    cursorEl.style.userSelect = 'none';
    cursorEl.hidden = true;

    document.body.appendChild(cursorEl);
    root.classList.add('tarte-custom-cursor-active');

    window.addEventListener('pointermove', handlePointerMove, { passive: true });
    document.addEventListener('mouseout', handleDocumentExit);
    document.addEventListener('mouseover', showCursor);
    document.addEventListener('focusin', showCursor);

    beamBridge.activate();

    if (isExpired()) {
      removeCursor();
      return;
    }

    const scheduleRemovalCheck = function() {
      if (Number.isNaN(endTimestamp) || endTimestamp <= 0 || isRemoved) {
        return;
      }

      const msUntilEnd = (endTimestamp * 1000) - Date.now();

      if (msUntilEnd <= 0) {
        removeCursor();
        return;
      }

      removalTimer = window.setTimeout(
        scheduleRemovalCheck,
        Math.min(msUntilEnd, maxTimerDuration)
      );
    };

    if (removalTimer) {
      window.clearTimeout(removalTimer);
    }

    scheduleRemovalCheck();
  };

  applyCursor();
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', window.initCustomCursor);
} else {
  window.initCustomCursor();
}
